import { useTranslation } from 'react-i18next';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Filter, FilterX, List, Table2, Clock, Download, AlertTriangle, XCircle, Star, ArrowRightLeft, Layers, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRole } from "@/hooks/use-role";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { PushToShopifyButton } from "./PushToShopifyButton";
import { PageActionBarDivider } from "@/components/layout/PageActionBar";
import { cn } from "@/lib/utils";
import { WhoCanUseFilter } from "@/features/base-garments/components/WhoCanUseFilter";
import { WHO_ANY, type WhoCanUseFilter as WhoFilter } from "@/features/base-garments/lib/base-garments-url";
import { audienceLabel } from "@/features/base-garments/lib/base-garments-audience";
import type { CountSheetSize } from "../utils/stocktakeCsv";

const NO_CLIENTS: ReadonlyArray<{ id: string; name: string }> = [];

interface Filters {
  type: string[];
  color: string[];
  size: string[];
}

interface InventoryTableFiltersProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  filters: Filters;
  setFilters: (filters: Filters) => void;
  stockFilter: 'low' | 'out' | 'key' | 'preprinted' | null;
  setStockFilter: (value: 'low' | 'out' | 'key' | 'preprinted' | null) => void;
  viewMode: 'list' | 'table';
  setViewMode: (mode: 'list' | 'table') => void;
  showStockAge: boolean;
  setShowStockAge: (show: boolean) => void;
  stockAgeLoading: boolean;
  handleDownloadCsv: () => void;
  onAddStockIndividualClick: () => void;
  onAddStockCsvClick: () => void;
  onStocktakeIndividualClick: () => void;
  onStocktakeCsvClick: () => void;
  onBulkThresholdCsvClick: () => void;
  selectedCount: number;
  selectedProductIds?: string[];
  onBulkAddStock: () => void;
  onSetStockSelected: () => void;
  onClearSelection: () => void;
  uniqueTypes: string[];
  uniqueColors: string[];
  uniqueSizes: string[];
  isFilterActive: boolean;
  handleClearFilters: () => void;
  onMoveToUnavailable: () => void;
  onMoveToAvailable: () => void;
  onAddPrePrintedStockClick: () => void;
  onReconcile?: () => void;
  /** Shared-inventory context used to fail closed on cross-org Shopify pushes. */
  pushViewedOrganizationId?: string | null;
  pushViewedOrganizationName?: string | null;
  pushHasMixedOrganizationRows?: boolean;
  stockActionsDisabled?: boolean;
  /**
   * BLU-711 A1: pre-printing is NOT covered by the client stock-write grant —
   * it flips production_type to PAP and changes hybrid fulfilment at the
   * OWNER's floor. Kept disabled for any client at a shared location, opted in
   * or not. (The endpoint itself has no owner guard — see BLU-711 follow-up.)
   *
   * BLU-717 S3 folds a second cause into the same flag: the selected location is
   * not permitted to pre-print at all (`production_settings.garment_preprint_enabled`).
   * `prePrintActionsDisabledReason` names which cause applies.
   */
  prePrintActionsDisabled?: boolean;
  /**
   * BLU-717 S3: why the pre-print item is disabled, rendered as visible text
   * beneath it (never hover-only). Defaults to the 3PL reason this gate
   * originally existed for, so an omitted reason can't leave a silently
   * disabled item.
   */
  prePrintActionsDisabledReason?: string;
  /**
   * BLU-3xx Phase 47.01.2-B-04: optional reason text for the disabled-state
   * warning. When the caller is a Shopify-client at a 3PL location they
   * cannot adjust stock — pass a reason like "Stock adjustments are managed
   * by your 3PL provider" instead of the default "select a location" copy.
   */
  stockActionsDisabledReason?: string;
  /**
   * What "Count sheet: all" writes for the filtered rows (`countSheetSize`):
   * the file's own line count, and the Just in time pairs it leaves out, so
   * the label never reads "all 8" over a 3-line file.
   */
  countSheetAll?: CountSheetSize;
  /**
   * ace/trims-ux: server-side type filter (POD / PAP / Trims). Distinct from
   * `filters.type` (garment style, e.g. "T-Shirt") above — this narrows the
   * fulfilment/production type via the backend query param.
   */
  productionTypeFilter?: 'PAP' | 'POD' | 'TRIM';
  onProductionTypeFilterChange?: (value: 'PAP' | 'POD' | 'TRIM' | undefined) => void;
  /** BLU-1394 F25: "Who can use it" for blanks, server-side. Rendered only with a change handler (3PL orgs). */
  whoFilter?: WhoFilter;
  onWhoFilterChange?: (value: WhoFilter) => void;
  whoClients?: ReadonlyArray<{ id: string; name: string }>;
  /** Stocktake count sheet exports — split out of the single Export button. */
  onExportCountSheetSelected: () => void;
  onExportCountSheetAll: () => void;
  onExportCountSheetExample: () => void;
  includeCurrentCounts: boolean;
  onToggleIncludeCurrentCounts: (v: boolean) => void;
  /**
   * The INTERSECTED selected-and-visible count (filtered by the cross-org
   * client filter + search/facets), never the raw `selectedCount` — otherwise
   * the menu label can read "42 selected" while the exported file has 37 rows.
   */
  stocktakeSelectedCount: number;
  /** What "Count sheet: N selected" writes for those rows; absent = `stocktakeSelectedCount` lines. */
  countSheetSelected?: CountSheetSize;
  /**
   * Count-sheet exports need a specific location (the sheet's Location
   * column + the anti-double-count guard in `useStocktakeExport`); the
   * generic "Stock data" export already carries its own toast-based guard.
   */
  hasSelectedLocation: boolean;
}

export function InventoryTableFilters({
  searchTerm,
  setSearchTerm,
  filters,
  setFilters,
  stockFilter,
  setStockFilter,
  viewMode,
  setViewMode,
  showStockAge,
  setShowStockAge,
  stockAgeLoading,
  handleDownloadCsv,
  onAddStockIndividualClick,
  onAddStockCsvClick,
  onStocktakeIndividualClick,
  onStocktakeCsvClick,
  onBulkThresholdCsvClick,
  selectedCount,
  selectedProductIds,
  onBulkAddStock,
  onSetStockSelected,
  onClearSelection,
  uniqueTypes,
  uniqueColors,
  uniqueSizes,
  isFilterActive,
  handleClearFilters,
  onMoveToUnavailable,
  onMoveToAvailable,
  onAddPrePrintedStockClick,
  onReconcile,
  pushViewedOrganizationId,
  pushViewedOrganizationName,
  pushHasMixedOrganizationRows = false,
  stockActionsDisabled = false,
  prePrintActionsDisabled = false,
  prePrintActionsDisabledReason,
  stockActionsDisabledReason,
  countSheetAll,
  productionTypeFilter,
  onProductionTypeFilterChange,
  whoFilter = WHO_ANY,
  onWhoFilterChange,
  whoClients = NO_CLIENTS,
  onExportCountSheetSelected,
  onExportCountSheetAll,
  onExportCountSheetExample,
  includeCurrentCounts,
  onToggleIncludeCurrentCounts,
  stocktakeSelectedCount,
  countSheetSelected,
  hasSelectedLocation,
}: InventoryTableFiltersProps) {
  const { t } = useTranslation('inventory');
  // "Count sheet: all 3 (5 Just in time left out)": the file's lines, and why there are fewer.
  const sheetAll = countSheetAll ?? { lines: 0, justInTime: 0 };
  const sheetSelected = countSheetSelected ?? { lines: stocktakeSelectedCount, justInTime: 0 };
  const countSheetAllLabel =
    sheetAll.justInTime > 0
      ? t('filters.exportCountSheetJustInTime', { count: sheetAll.lines, justInTime: sheetAll.justInTime })
      : t('filters.exportCountSheet', { count: sheetAll.lines });
  const countSheetSelectedLabel =
    sheetSelected.justInTime > 0
      ? t('filters.exportCountSheetSelectedJustInTime', {
          count: sheetSelected.lines,
          justInTime: sheetSelected.justInTime,
        })
      : t('filters.exportCountSheetSelected', { count: sheetSelected.lines });
  const { can } = useRole();
  const isAdmin = can('skus.manage');
  const utils = trpc.useUtils();

  const bulkSetKeyItemsMutation = trpc.inventory.bulkSetKeyItems.useMutation({
    onSuccess: (_data, input) => {
      toast.success(`${input.skuIds.length} items ${input.isKeyItem ? 'marked as' : 'removed from'} key items`);
      void utils.inventory.getProducts.invalidate();
    },
    onError: (error) => {
      toast.error('Failed to update key items', {
        description: error.message,
      });
    },
  });

  const handleFilterChange = (filterType: keyof Filters, value: string) => {
    setFilters({
      ...filters,
      [filterType]: filters[filterType].includes(value)
        ? filters[filterType].filter(v => v !== value)
        : [...filters[filterType], value]
    });
  };

  const handleSelectAllFilter = (filterType: keyof Filters, items: string[]) => {
    if (filters[filterType].length === items.length) {
      setFilters({ ...filters, [filterType]: [] });
    } else {
      setFilters({ ...filters, [filterType]: items });
    }
  };

  const hasAdvancedFilters =
    filters.type.length > 0 || filters.color.length > 0 || filters.size.length > 0;

  const stockFilterLabel = stockFilter === 'low'
    ? t('filters.lowStock')
    : stockFilter === 'out'
      ? t('filters.outOfStock')
      : stockFilter === 'key'
        ? t('filters.keyItems')
        : stockFilter === 'preprinted'
          ? t('filters.prePrinted')
          : null;

  const productionTypeLabel = productionTypeFilter === 'POD'
    ? t('filters.productionTypePod')
    : productionTypeFilter === 'PAP'
      ? t('filters.productionTypePap')
      : productionTypeFilter === 'TRIM'
        ? t('filters.productionTypeTrims')
        : null;

  const activeFilterChips: Array<{
    key: string;
    label: string;
    onRemove: () => void;
  }> = [];

  if (stockFilterLabel) {
    activeFilterChips.push({
      key: 'stock',
      label: stockFilterLabel,
      onRemove: () => setStockFilter(null),
    });
  }
  if (productionTypeLabel) {
    activeFilterChips.push({
      key: 'production-type',
      label: productionTypeLabel,
      onRemove: () => onProductionTypeFilterChange?.(undefined),
    });
  }
  if (onWhoFilterChange && whoFilter.kind !== 'any') {
    const clientName =
      whoFilter.kind === 'client'
        ? (whoClients.find((client) => client.id === whoFilter.clientOrganizationId)?.name ?? null)
        : null;
    activeFilterChips.push({
      key: 'who-can-use',
      label: `${t('filters.whoCanUse')}: ${audienceLabel(t, whoFilter.kind, clientName)}`,
      onRemove: () => onWhoFilterChange(WHO_ANY),
    });
  }
  if (filters.type.length > 0) {
    activeFilterChips.push({
      key: 'type',
      label: `${t('filters.type')}: ${filters.type.join(', ')}`,
      onRemove: () => setFilters({ ...filters, type: [] }),
    });
  }
  if (filters.color.length > 0) {
    activeFilterChips.push({
      key: 'color',
      label: `${t('filters.color')}: ${filters.color.join(', ')}`,
      onRemove: () => setFilters({ ...filters, color: [] }),
    });
  }
  if (filters.size.length > 0) {
    activeFilterChips.push({
      key: 'size',
      label: `${t('filters.size')}: ${filters.size.join(', ')}`,
      onRemove: () => setFilters({ ...filters, size: [] }),
    });
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-2">
        {/* Find and filter: one primary row, allowed to wrap without mixing in
            write actions. PageActionBar measures the final height so the table
            header remains correctly pinned below it. */}
        <div
          role="group"
          aria-label={t('filters.searchAndFilters')}
          className="flex flex-wrap items-center gap-2"
        >
          <div className="relative min-w-0 basis-full sm:min-w-[18rem] sm:flex-1 xl:max-w-[32rem]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder={t('filters.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={stockFilter === 'low'}
              onClick={() => setStockFilter(stockFilter === 'low' ? null : 'low')}
              className={cn(
                "h-8 gap-1.5 px-2.5 text-xs whitespace-nowrap transition-colors duration-200 motion-reduce:transition-none",
                stockFilter === 'low' && "border-yellow-500 bg-yellow-500 text-white hover:bg-yellow-600 hover:text-white",
              )}
            >
              <AlertTriangle className="h-3 w-3" />
              {t('filters.lowStock')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={stockFilter === 'out'}
              onClick={() => setStockFilter(stockFilter === 'out' ? null : 'out')}
              className={cn(
                "h-8 gap-1.5 px-2.5 text-xs whitespace-nowrap transition-colors duration-200 motion-reduce:transition-none",
                stockFilter === 'out' && "border-red-500 bg-red-500 text-white hover:bg-red-600 hover:text-white",
              )}
            >
              <XCircle className="h-3 w-3" />
              {t('filters.outOfStock')}
            </Button>
            {isAdmin && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-pressed={stockFilter === 'key'}
                onClick={() => setStockFilter(stockFilter === 'key' ? null : 'key')}
                className={cn(
                  "h-8 gap-1.5 px-2.5 text-xs whitespace-nowrap transition-colors duration-200 motion-reduce:transition-none",
                  stockFilter === 'key' && "border-amber-500 bg-amber-500 text-white hover:bg-amber-600 hover:text-white",
                )}
              >
                <Star className="h-3 w-3" />
                {t('filters.keyItems')}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={stockFilter === 'preprinted'}
              onClick={() => setStockFilter(stockFilter === 'preprinted' ? null : 'preprinted')}
              className={cn(
                "h-8 gap-1.5 px-2.5 text-xs whitespace-nowrap transition-colors duration-200 motion-reduce:transition-none",
                stockFilter === 'preprinted' && "border-threadheads-purple bg-threadheads-purple text-white hover:bg-threadheads-purple/90 hover:text-white",
              )}
            >
              <Layers className="h-3 w-3" />
              {t('filters.prePrinted')}
            </Button>
          </div>

          {/* ace/trims-ux: server-side type filter (POD / PAP / Trims). Sits
              beside the category (garment-style) filter in the Advanced
              filter popover below — this one narrows the fulfilment/
              production type via the backend query param. */}
          {onProductionTypeFilterChange && (
            <Select
              value={productionTypeFilter ?? 'all'}
              onValueChange={(value) =>
                onProductionTypeFilterChange(
                  value === 'all' ? undefined : (value as 'PAP' | 'POD' | 'TRIM')
                )
              }
            >
              <SelectTrigger className="h-8 w-[160px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filters.productionTypeAll')}</SelectItem>
                <SelectItem value="POD">{t('filters.productionTypePod')}</SelectItem>
                <SelectItem value="PAP">{t('filters.productionTypePap')}</SelectItem>
                <SelectItem value="TRIM">{t('filters.productionTypeTrims')}</SelectItem>
              </SelectContent>
            </Select>
          )}

          {/* BLU-1394 F25: who can use a blank, sent to the server like the
              type filter (3PL orgs only: the page passes no handler otherwise). */}
          {onWhoFilterChange && (
            <WhoCanUseFilter value={whoFilter} onChange={onWhoFilterChange} clients={whoClients} />
          )}

          {/* Advanced filter popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant={hasAdvancedFilters ? 'default' : 'outline'}
                size="sm"
                className={cn(
                  "h-8 gap-1.5 px-2.5 text-xs",
                  hasAdvancedFilters && "bg-threadheads-purple text-white hover:bg-threadheads-purple/90",
                )}
              >
                {hasAdvancedFilters ? <FilterX className="h-3.5 w-3.5" /> : <Filter className="h-3.5 w-3.5" />}
                {t('filters.moreFilters')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[calc(100vw-2rem)] max-w-[50rem] p-4">
              <div className="space-y-4">
                <h4 className="font-medium text-sm">{t('filters.filterInventory')}</h4>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>{t('filters.type')}</Label>
                  <div className="border rounded-md p-2 max-h-40 overflow-y-auto">
                    {uniqueTypes.length > 0 ? (
                      uniqueTypes.map(type => (
                        <div key={type} className="flex items-center space-x-2 py-1">
                          <Checkbox
                            id={`type-${type}`}
                            checked={filters.type.includes(type)}
                            onCheckedChange={() => handleFilterChange("type", type)}
                          />
                          <label htmlFor={`type-${type}`} className="text-sm cursor-pointer select-none flex-1">{type}</label>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">{t('filters.noTypesAvailable')}</p>
                    )}
                  </div>
                  {uniqueTypes.length > 0 && (
                    <Button variant="ghost" size="sm" className="w-full" onClick={() => handleSelectAllFilter("type", uniqueTypes)}>
                      {filters.type.length === uniqueTypes.length ? t('filters.deselectAll') : t('filters.selectAll')}
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t('filters.color')}</Label>
                  <div className="border rounded-md p-2 max-h-40 overflow-y-auto">
                    {uniqueColors.length > 0 ? (
                      uniqueColors.map(color => (
                        <div key={color} className="flex items-center space-x-2 py-1">
                          <Checkbox
                            id={`color-${color}`}
                            checked={filters.color.includes(color)}
                            onCheckedChange={() => handleFilterChange("color", color)}
                          />
                          <label htmlFor={`color-${color}`} className="text-sm cursor-pointer select-none flex-1">{color}</label>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">{t('filters.noColorsAvailable')}</p>
                    )}
                  </div>
                  {uniqueColors.length > 0 && (
                    <Button variant="ghost" size="sm" className="w-full" onClick={() => handleSelectAllFilter("color", uniqueColors)}>
                      {filters.color.length === uniqueColors.length ? t('filters.deselectAll') : t('filters.selectAll')}
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t('filters.size')}</Label>
                  <div className="border rounded-md p-2 max-h-40 overflow-y-auto">
                    {uniqueSizes.length > 0 ? (
                      uniqueSizes.map(size => (
                        <div key={size} className="flex items-center space-x-2 py-1">
                          <Checkbox
                            id={`size-${size}`}
                            checked={filters.size.includes(size)}
                            onCheckedChange={() => handleFilterChange("size", size)}
                          />
                          <label htmlFor={`size-${size}`} className="text-sm cursor-pointer select-none flex-1">{size}</label>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">{t('filters.noSizesAvailable')}</p>
                    )}
                  </div>
                  {uniqueSizes.length > 0 && (
                    <Button variant="ghost" size="sm" className="w-full" onClick={() => handleSelectAllFilter("size", uniqueSizes)}>
                      {filters.size.length === uniqueSizes.length ? t('filters.deselectAll') : t('filters.selectAll')}
                    </Button>
                  )}
                </div>
                </div>
                <div className="flex justify-end pt-2 border-t">
                  <Button variant="outline" size="sm" onClick={handleClearFilters} disabled={!isFilterActive}>
                    {t('filters.clearAllFilters')}
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Viewing controls and write actions have their own row. This keeps
            operational actions stable on the right instead of letting them
            wrap into the filter sequence. */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <div
              role="group"
              aria-label={t('filters.viewOptions')}
              className="inline-flex shrink-0 items-center rounded-md border bg-muted/40 p-0.5"
            >
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-pressed={viewMode === 'list'}
                onClick={() => setViewMode('list')}
                className={cn(
                  "h-7 gap-1.5 rounded-sm px-2 text-xs transition-colors duration-200 motion-reduce:transition-none",
                  viewMode === 'list' && "bg-background text-foreground shadow-sm hover:bg-background",
                )}
              >
                <List className="h-3.5 w-3.5" />
                {t('filters.listView')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-pressed={viewMode === 'table'}
                onClick={() => setViewMode('table')}
                className={cn(
                  "h-7 gap-1.5 rounded-sm px-2 text-xs transition-colors duration-200 motion-reduce:transition-none",
                  viewMode === 'table' && "bg-background text-foreground shadow-sm hover:bg-background",
                )}
              >
                <Table2 className="h-3.5 w-3.5" />
                {t('filters.matrixView')}
              </Button>
            </div>

            {viewMode === 'table' && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant={showStockAge ? 'default' : 'outline'}
                      size="sm"
                      aria-pressed={showStockAge}
                      onClick={() => setShowStockAge(!showStockAge)}
                      disabled={stockAgeLoading}
                      className={cn(
                        "h-8 gap-1.5 px-2.5 text-xs",
                        showStockAge && "bg-threadheads-purple text-white hover:bg-threadheads-purple/90",
                      )}
                    >
                      <Clock className="h-3.5 w-3.5" />
                      {t('filters.stockAge')}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('filters.stockAgeTooltip')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {activeFilterChips.length > 0 && (
              <>
                <PageActionBarDivider />
                <div
                  role="group"
                  aria-label={t('filters.activeFilters')}
                  className="flex min-w-0 flex-wrap items-center gap-1.5"
                >
                  <span className="text-[11px] font-medium text-muted-foreground">
                    {t('filters.activeFilters')}
                  </span>
                  {activeFilterChips.map((chip) => (
                    <button
                      key={chip.key}
                      type="button"
                      onClick={chip.onRemove}
                      aria-label={t('filters.removeFilter', { filter: chip.label })}
                      className="inline-flex h-7 max-w-[18rem] items-center gap-1 rounded-md bg-muted px-2 text-xs text-foreground transition-colors duration-200 hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
                    >
                      <span className="truncate">{chip.label}</span>
                      <X className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
                    </button>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                    className="h-7 px-2 text-xs text-muted-foreground"
                  >
                    {t('filters.clearAllFilters')}
                  </Button>
                </div>
              </>
            )}
          </div>

          <div
            role="group"
            aria-label={t('filters.inventoryActions')}
            className="ml-auto flex w-full shrink-0 flex-wrap items-center justify-end gap-1.5 lg:w-auto"
          >
            {onReconcile && (
              <Button
                variant="outline"
                size="sm"
                onClick={onReconcile}
                className="h-8 text-xs"
              >
                {t('filters.reconcile')}
              </Button>
            )}
            <PushToShopifyButton
              viewedOrganizationId={pushViewedOrganizationId}
              viewedOrganizationName={pushViewedOrganizationName}
              hasMixedOrganizationRows={pushHasMixedOrganizationRows}
            />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1.5"
              >
                <Download className="h-3 w-3" />
                {t('filters.exportMenuLabel')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleDownloadCsv}>
                {t('filters.exportStockData')}
              </DropdownMenuItem>
              {viewMode === 'list' && stocktakeSelectedCount > 0 && (
                <DropdownMenuItem onClick={onExportCountSheetSelected} disabled={!hasSelectedLocation}>
                  {countSheetSelectedLabel}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={onExportCountSheetAll}
                disabled={!hasSelectedLocation || sheetAll.lines === 0}
              >
                {countSheetAllLabel}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onExportCountSheetExample}>
                {t('filters.exportCountSheetExample')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {/* Blank is the safe default: rows the counter never fills in are
                  never written. Pre-filling is opt-in and deliberately explained,
                  because a stale pre-filled sheet re-imported hours later would
                  write every untouched row back to its export-time value. */}
              <DropdownMenuCheckboxItem
                checked={includeCurrentCounts}
                onCheckedChange={(checked) => onToggleIncludeCurrentCounts(checked === true)}
                onSelect={(e) => e.preventDefault()}
              >
                {t('stocktakeExport.includeCurrentCounts')}
              </DropdownMenuCheckboxItem>
              <p className="px-2 pb-1.5 pt-0.5 text-[11px] leading-snug text-muted-foreground max-w-[15rem]">
                {t('stocktakeExport.includeCurrentCountsHint')}
              </p>
            </DropdownMenuContent>
            </DropdownMenu>

            <PageActionBarDivider />

            {/* BLU-3xx Phase 47.01.2-B-04: when stock actions are disabled for
                a known reason (caller is a Shopify-client at a 3PL location),
                wrap the trigger in a Tooltip so the operator gets immediate
                in-context explanation instead of clicking through to find a
                dimmed dropdown. The all-items-disabled-but-trigger-still-clicks
                anti-pattern is exactly what the read-only gate in CONTEXT.md
                calls out. */}
            <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* span wrapper lets the disabled button still trigger tooltip */}
                <span tabIndex={stockActionsDisabled && stockActionsDisabledReason ? 0 : -1}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        disabled={stockActionsDisabled && !!stockActionsDisabledReason}
                        className="h-8 text-xs bg-threadheads-purple text-white hover:bg-threadheads-purple/90 disabled:opacity-50"
                      >
                        {t('filters.adjustStock')}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>{t('filters.addStockLabel')}</DropdownMenuLabel>
                      <DropdownMenuItem onClick={onAddStockIndividualClick} disabled={stockActionsDisabled}>{t('filters.individual')}</DropdownMenuItem>
                      <DropdownMenuItem onClick={onAddStockCsvClick} disabled={stockActionsDisabled}>{t('filters.uploadCsv')}</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>{t('filters.stocktake')}</DropdownMenuLabel>
                      <DropdownMenuItem onClick={onStocktakeIndividualClick} disabled={stockActionsDisabled}>{t('filters.individual')}</DropdownMenuItem>
                      <DropdownMenuItem onClick={onStocktakeCsvClick} disabled={stockActionsDisabled}>{t('filters.uploadCsv')}</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>{t('filters.thresholds')}</DropdownMenuLabel>
                      <DropdownMenuItem onClick={onBulkThresholdCsvClick} disabled={stockActionsDisabled}>{t('filters.bulkUpdateCsv')}</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>{t('filters.unavailableLabel')}</DropdownMenuLabel>
                      <DropdownMenuItem onClick={onMoveToUnavailable} disabled={stockActionsDisabled}>{t('filters.moveToUnavailable')}</DropdownMenuItem>
                      <DropdownMenuItem onClick={onMoveToAvailable} disabled={stockActionsDisabled}>{t('filters.releaseToAvailable')}</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>{t('filters.prePrintedLabel')}</DropdownMenuLabel>
                      <DropdownMenuItem
                        onClick={onAddPrePrintedStockClick}
                        disabled={stockActionsDisabled || prePrintActionsDisabled}
                      >
                        <ArrowRightLeft className="mr-2 h-4 w-4" />
                        {t('filters.addPrePrintedStock')}
                      </DropdownMenuItem>
                      {/* BLU-711 / BLU-717 S3: when the caller CAN adjust stock but
                          pre-printing is unavailable here, this is the lone disabled
                          item in an otherwise-live menu — say why, as visible text
                          rather than a tooltip (mirrors the stocktake-export hint
                          idiom). Two causes share the item: the 3PL default below,
                          and "not enabled at this floor" passed in by the page. */}
                      {prePrintActionsDisabled && !stockActionsDisabled && (
                        <p
                          role="note"
                          className="px-2 pb-1.5 pt-0.5 text-[11px] leading-snug text-muted-foreground max-w-[15rem]"
                        >
                          {prePrintActionsDisabledReason ??
                            'Pre-printed stock at this location is managed by your 3PL provider.'}
                        </p>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </span>
              </TooltipTrigger>
              {stockActionsDisabled && stockActionsDisabledReason && (
                <TooltipContent side="bottom" className="max-w-xs">
                  {stockActionsDisabledReason}
                </TooltipContent>
              )}
            </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      {/* Location warning */}
      {stockActionsDisabled && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {stockActionsDisabledReason ?? t('filters.allLocationsWarning')}
        </div>
      )}

      {/* Selection bar — only visible when items are selected (no "0 selected" dead space) */}
      {viewMode === 'list' && selectedCount > 0 && (
        <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-1.5">
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <span className="font-medium text-foreground">{t('filters.selectedCount', { count: selectedCount })}</span>
            <Button variant="link" size="sm" className="px-1 h-auto text-xs" onClick={onClearSelection}>
              {t('filters.clearBtn')}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onBulkAddStock} disabled={stockActionsDisabled}>
              {selectedCount > 1 ? t('filters.addStockToSelected') : t('filters.addStockSingle')}
            </Button>
            {selectedCount === 1 && (
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onSetStockSelected}>
                {t('filters.setStock')}
              </Button>
            )}
            {isAdmin && selectedProductIds && selectedProductIds.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  disabled={bulkSetKeyItemsMutation.isPending}
                  onClick={() => bulkSetKeyItemsMutation.mutate({ skuIds: selectedProductIds, isKeyItem: true })}
                >
                  <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                  {t('filters.markAsKeyItems')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  disabled={bulkSetKeyItemsMutation.isPending}
                  onClick={() => bulkSetKeyItemsMutation.mutate({ skuIds: selectedProductIds, isKeyItem: false })}
                >
                  <Star className="h-3 w-3 text-muted-foreground/40" />
                  {t('filters.removeKeyItemStatus')}
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
